import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { WebPageSchema, BreadcrumbSchema, ServiceSchema } from '../components/SEOStructuredData';
import { Container, Title, Text, SimpleGrid, List, ThemeIcon, Card, Grid, Button, Badge, Group } from '@mantine/core';
import { motion } from 'framer-motion';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCheck,
  IconCode,
  IconColorSwatch,
  IconDeviceAnalytics,
  IconGitBranch,
  IconLayoutDashboard,
  IconPuzzle,
  IconRocket,
  IconShieldCheck,
  IconUsers,
  IconUsersGroup
} from '@tabler/icons-react';
import classes from './ControlForge.module.css';

export default function ControlForge() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { name: 'Home', url: 'https://optifyne.com/' },
    { name: 'Products', url: 'https://optifyne.com/products' },
    { name: 'Control Forge', url: 'https://optifyne.com/products/control-forge' }
  ];

  const problems = [
    'Assumes deep JavaScript knowledge',
    'Encourages one-off implementations',
    'Makes consistency hard across teams',
    'Pushes testing, analysis, and standards outside the platform',
    'Leads to "every developer builds it differently"'
  ];

  const capabilities = [
    {
      icon: IconLayoutDashboard,
      title: 'Structured control design',
      description: 'A structured way to design custom controls with defined patterns and lifecycles.'
    },
    {
      icon: IconPuzzle,
      title: 'Low-code assembly',
      description: 'Assemble behaviour and properties using a low-code approach rather than raw JavaScript.'
    },
    {
      icon: IconShieldCheck,
      title: 'Embedded best practices',
      description: 'Built-in guidelines, patterns, and best practices that teams follow automatically.'
    },
    {
      icon: IconDeviceAnalytics,
      title: 'Analysis and testing',
      description: 'Integrated support for analysis, testing, and versioning of every control.'
    },
    {
      icon: IconColorSwatch,
      title: 'Theming layer',
      description: 'Adapt controls for different user needs — GDS, corporate branding, or accessibility variants.'
    },
    {
      icon: IconGitBranch,
      title: 'Version control by default',
      description: 'Every control is versioned, traceable, reproducible, and governed from day one.'
    }
  ];

  const lowCodeFeatures = [
    'Predefined control lifecycles',
    'Standardised property definitions',
    'Consistent validation behaviour',
    'Predictable SmartObject interaction patterns',
    'Shared accessibility and UX rules'
  ];

  const guardrails = [
    'Naming conventions',
    'Property exposure patterns',
    'Visibility, read-only, and validation handling',
    'Error and state management',
    'Accessibility expectations',
    'Documentation structure'
  ];

  const qualityFeatures = [
    'Control-level analysis and validation',
    'Repeatable testing patterns',
    'Versioned artefacts',
    'Clear upgrade paths',
    'Backwards-compatibility awareness'
  ];

  const versionFeatures = [
    'Multiple teams working in parallel',
    'Long-lived enterprise applications',
    'Controlled rollout of improvements',
    'Clear ownership and auditability'
  ];

  const themingOptions = [
    'Government Digital Service (GDS) styling',
    'Internal corporate branding',
    'High-contrast or accessibility-focused variants',
    'Simplified or task-specific UI patterns'
  ];

  const builtFor = [
    { icon: IconUsersGroup, label: 'Centres of Excellence' },
    { icon: IconUsers, label: 'Multi-team delivery environments' },
    { icon: IconCode, label: 'Long-running K2 estates' },
    { icon: IconShieldCheck, label: 'Regulated organisations' }
  ];

  const typicalUses = [
    'Establishing a custom control standard across K2 teams',
    'Enabling junior developers to build controls safely',
    'Accelerating delivery without sacrificing quality',
    'Modernising SmartForms UX incrementally',
    'Supporting multiple UI standards from one framework'
  ];

  return (
    <>
      <Helmet>
        <title>Control Forge - Low-Code Nintex K2 Custom Control Framework | Optifyne</title>
        <meta name="description" content="Control Forge is Optifyne's low-code framework for building enterprise-grade Nintex K2 custom controls. Faster, safer, and more consistent control development at scale." />
      </Helmet>
      <WebPageSchema
        title="Control Forge - Low-Code Nintex K2 Custom Control Framework"
        description="Control Forge is Optifyne's low-code framework for building enterprise-grade Nintex K2 custom controls. Faster, safer, and more consistent control development at scale."
        url="https://optifyne.com/products/control-forge"
      />
      <ServiceSchema
        name="Control Forge"
        description="A low-code framework for building enterprise-grade Nintex K2 custom controls, making development faster, safer, and more consistent for teams at scale."
        url="https://optifyne.com/products/control-forge"
      />
      <BreadcrumbSchema items={breadcrumbItems} />

      <div className={classes.wrapper}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className={classes.heroContent}
            >
              <Badge className={classes.badge} size="lg">Product</Badge>
              <Title className={classes.title}>
                <span className={classes.highlight}>Control Forge</span>
              </Title>
              <Text className={classes.subtitle}>
                A low-code framework for building enterprise-grade Nintex K2 custom controls
              </Text>
              <Text className={classes.subtitle} mt="lg">
                Control Forge sits on top of Nintex's JavaScript Custom Control Framework, making it faster, safer, and more consistent for teams to design, build, test, and evolve custom controls at scale.
              </Text>
            </motion.div>
          </Container>
        </section>

        {/* The Problem Section */}
        <section className={classes.problemSection}>
          <Container size="xl">
            <Grid gutter={{ base: 20, md: 50 }} align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Title order={2} className={classes.sectionTitle} mb="md">
                    The problem Control Forge solves
                  </Title>
                  <Text className={classes.sectionText} mb="xl">
                    Nintex's JavaScript Custom Control Framework is powerful — but in its raw form it creates challenges for teams working at scale.
                  </Text>
                  <List
                    spacing="md"
                    size="lg"
                    className={classes.problemList}
                    icon={
                      <ThemeIcon size={24} radius="xl" className={classes.problemIcon}>
                        <IconAlertTriangle size={14} />
                      </ThemeIcon>
                    }
                  >
                    {problems.map((problem, index) => (
                      <List.Item key={index}>{problem}</List.Item>
                    ))}
                  </List>
                </motion.div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <div className={classes.featureHighlight}>
                    <Title order={3} className={classes.featureHighlightTitle}>
                      The solution
                    </Title>
                    <Text className={classes.featureHighlightText}>
                      Control Forge addresses this by providing a low-code abstraction, shared patterns, and built-in guardrails — without hiding or limiting the underlying framework.
                    </Text>
                    <Text className={classes.featureHighlightText} mt="md">
                      It turns custom control development from a bespoke, developer-led activity into a repeatable, governed capability that entire teams can use with confidence.
                    </Text>
                  </div>
                </motion.div>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* What Control Forge Is Section */}
        <section className={classes.whatSection}>
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Title order={2} className={classes.sectionTitle} ta="center" mb="sm">
                What Control Forge actually is
              </Title>
              <Text className={classes.sectionText} ta="center" maw={700} mx="auto" mb="xl">
                Control Forge is not just a library of controls. It is a control development platform that provides:
              </Text>
            </motion.div>

            <SimpleGrid
              cols={{ base: 1, sm: 2, lg: 3 }}
              spacing="xl"
              verticalSpacing="xl"
            >
              {capabilities.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <Card className={classes.capabilityCard}>
                    <ThemeIcon size={48} radius="md" className={classes.capabilityIcon}>
                      <item.icon size={24} />
                    </ThemeIcon>
                    <Title order={4} className={classes.capabilityTitle}>
                      {item.title}
                    </Title>
                    <Text className={classes.capabilityDescription}>
                      {item.description}
                    </Text>
                  </Card>
                </motion.div>
              ))}
            </SimpleGrid>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Text className={classes.callout} mt={40}>
                Think of it as a foundry where controls are forged consistently — not handcrafted differently every time.
              </Text>
            </motion.div>
          </Container>
        </section>

        {/* Low-Code Building Section */}
        <section className={classes.featureSection}>
          <Container size="xl">
            <Grid gutter={{ base: 20, md: 50 }} align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Title order={2} className={classes.sectionTitle} mb="md">
                    Low-code control building
                  </Title>
                  <Text className={classes.sectionText} fw={600} mb="md">
                    Not low standards.
                  </Text>
                  <Text className={classes.sectionText} mb="xl">
                    With Control Forge, teams build controls using defined patterns rather than raw JavaScript from scratch. Developers focus on what the control does, not re-implementing plumbing, lifecycle logic, or edge-case handling.
                  </Text>
                  <List
                    spacing="md"
                    size="lg"
                    className={classes.featureList}
                    icon={
                      <ThemeIcon size={24} radius="xl" className={classes.featureIcon}>
                        <IconCheck size={14} />
                      </ThemeIcon>
                    }
                  >
                    {lowCodeFeatures.map((feature, index) => (
                      <List.Item key={index}>{feature}</List.Item>
                    ))}
                  </List>
                </motion.div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Title order={2} className={classes.sectionTitle} mb="md">
                    Built-in standards and guardrails
                  </Title>
                  <Text className={classes.sectionText} mb="xl">
                    Control Forge embeds rules so teams don't need to remember them. This ensures every control behaves like it belongs to the same platform, regardless of who built it.
                  </Text>
                  <List
                    spacing="md"
                    size="lg"
                    className={classes.featureList}
                    icon={
                      <ThemeIcon size={24} radius="xl" className={classes.featureIcon}>
                        <IconShieldCheck size={14} />
                      </ThemeIcon>
                    }
                  >
                    {guardrails.map((item, index) => (
                      <List.Item key={index}>{item}</List.Item>
                    ))}
                  </List>
                </motion.div>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* Quality & Versioning Section */}
        <section className={classes.featureSectionAlt}>
          <Container size="xl">
            <Grid gutter={{ base: 20, md: 50 }} align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Title order={2} className={classes.sectionTitle} mb="md">
                    Analysis, testing, and quality
                  </Title>
                  <Text className={classes.sectionText} mb="xl">
                    Custom controls often fail not at build time, but months later. Control Forge brings quality forward, making custom controls safe to evolve — not fragile assets teams are afraid to touch.
                  </Text>
                  <List
                    spacing="md"
                    size="lg"
                    className={classes.featureList}
                    icon={
                      <ThemeIcon size={24} radius="xl" className={classes.featureIcon}>
                        <IconDeviceAnalytics size={14} />
                      </ThemeIcon>
                    }
                  >
                    {qualityFeatures.map((feature, index) => (
                      <List.Item key={index}>{feature}</List.Item>
                    ))}
                  </List>
                </motion.div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Title order={2} className={classes.sectionTitle} mb="md">
                    Version control by default
                  </Title>
                  <Text className={classes.sectionText} mb="md">
                    Every control built in Control Forge is designed to be versioned, traceable, reproducible, and governed. No more mystery JavaScript sitting in a ZIP file.
                  </Text>
                  <Text className={classes.sectionText} mb="xl">
                    This supports:
                  </Text>
                  <List
                    spacing="md"
                    size="lg"
                    className={classes.featureList}
                    icon={
                      <ThemeIcon size={24} radius="xl" className={classes.featureIcon}>
                        <IconGitBranch size={14} />
                      </ThemeIcon>
                    }
                  >
                    {versionFeatures.map((feature, index) => (
                      <List.Item key={index}>{feature}</List.Item>
                    ))}
                  </List>
                </motion.div>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* Theming Section */}
        <section className={classes.featureSection}>
          <Container size="xl">
            <Grid gutter={{ base: 20, md: 50 }} align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Title order={2} className={classes.sectionTitle} mb="md">
                    Theming as a first-class capability
                  </Title>
                  <Text className={classes.sectionText} mb="xl">
                    Control Forge separates behaviour from presentation. The same control logic can be reused across multiple visual themes, without rewriting code.
                  </Text>
                  <List
                    spacing="md"
                    size="lg"
                    className={classes.featureList}
                    icon={
                      <ThemeIcon size={24} radius="xl" className={classes.featureIcon}>
                        <IconColorSwatch size={14} />
                      </ThemeIcon>
                    }
                  >
                    {themingOptions.map((option, index) => (
                      <List.Item key={index}>{option}</List.Item>
                    ))}
                  </List>
                </motion.div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <div className={classes.featureHighlight}>
                    <Title order={3} className={classes.featureHighlightTitle}>
                      Built for teams, not heroes
                    </Title>
                    <Text className={classes.featureHighlightText}>
                      Control Forge reduces reliance on individual experts and replaces it with shared capability. It is designed for Centres of Excellence, multi-team delivery environments, long-running K2 estates, and regulated organisations.
                    </Text>
                  </div>
                </motion.div>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* Built For Section */}
        <section className={classes.builtForSection}>
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Title order={2} className={classes.sectionTitle} ta="center" mb="sm">
                Built for teams, not heroes
              </Title>
              <Text className={classes.sectionText} ta="center" maw={600} mx="auto" mb={40}>
                Control Forge reduces reliance on individual experts and replaces it with shared capability.
              </Text>
            </motion.div>

            <SimpleGrid cols={{ base: 2, md: 4 }} spacing="xl">
              {builtFor.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  <Card className={classes.builtForCard}>
                    <ThemeIcon size={48} radius="md" className={classes.builtForIcon}>
                      <item.icon size={24} />
                    </ThemeIcon>
                    <Text className={classes.builtForTitle}>{item.label}</Text>
                  </Card>
                </motion.div>
              ))}
            </SimpleGrid>
          </Container>
        </section>

        {/* Typical Uses Section */}
        <section className={classes.usesSection}>
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Title order={2} className={classes.sectionTitle} ta="center" mb={40}>
                Typical uses of Control Forge
              </Title>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <List
                spacing="lg"
                size="lg"
                className={classes.usesList}
                icon={
                  <ThemeIcon size={28} radius="xl" className={classes.usesIcon}>
                    <IconRocket size={16} />
                  </ThemeIcon>
                }
              >
                {typicalUses.map((use, index) => (
                  <List.Item key={index}>{use}</List.Item>
                ))}
              </List>
            </motion.div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className={classes.ctaSection}>
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className={classes.ctaContent}
            >
              <Title order={2} className={classes.sectionTitle}>
                Built on Nintex. Delivered by <span className={classes.highlight}>Optifyne</span>
              </Title>
              <Text className={classes.ctaText}>
                We created Control Forge because we've seen what happens when custom controls grow without structure — and what's possible when they're treated as a product, not an afterthought.
              </Text>
              <Group justify="center">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ display: 'inline-block' }}
                >
                  <Button
                    component={Link}
                    to="/contact"
                    size="xl"
                    className={classes.ctaButton}
                    rightSection={<IconArrowRight size={20} />}
                  >
                    Get in Touch
                  </Button>
                </motion.div>
              </Group>
            </motion.div>
          </Container>
        </section>
      </div>
    </>
  );
}
